<template>
    <div id="pleating-overview-page">
        <div class="card colors-list">
            <div class="card-content">
                <crm-table
                    v-if="initialFilters && definitions"
                    ref="canvasWorkOverviewTable"
                    :default-filters="initialFilters"
                    :columns="columns"
                    :items="products"
                    :count="products.length + 1"
                    :status="status"
                    :use-global-scroll="true"
                    @fetch="onFetchDebounced"
                    :get-row-style="getRowStyle"
                    :has-sticky="true"
                >
                    <template v-slot:no-results>
                        {{
                            trans(
                                'pages.canvas_work_overview.no_products_found'
                            )
                        }}
                    </template>
                    <template v-slot:filter-clearFilters="props">
                        <ui5-button @click="clearFilters">
                            {{ trans('pages.canvas_work_overview.reset') }}
                        </ui5-button>
                    </template>
                    <template v-slot:filter-sortEmpty="props">
                        <ui5-select
                            @change="
                                props.onInput(
                                    props.column.name,
                                    $event.detail.selectedOption.value
                                )
                            "
                        >
                            <ui5-option
                                v-for="option in sortEmptyOptions"
                                :key="'sortEmptyOption.' + option.value"
                                :value="option.value"
                                :selected="option.value == props.value"
                            >
                                {{ option.label }}
                            </ui5-option>
                        </ui5-select>
                    </template>
                    <template v-slot:filter-productlistDefinitionKey="props">
                        <ui5-select
                            @change="
                                props.onInput(
                                    props.column.name,
                                    $event.detail.selectedOption.value
                                )
                            "
                        >
                            <ui5-option
                                v-for="option in definitionOptions"
                                :key="
                                    'productlistDefinitionKey.' + option.value
                                "
                                :value="option.value"
                                :selected="option.value == props.value"
                            >
                                {{ option.label }}
                            </ui5-option>
                        </ui5-select>
                    </template>
                    <template v-slot:filter-deliveryDate="props">
                        <ui5-date-picker
                            format-pattern="yyyy-MM-dd"
                            :value="props.value"
                            @change="
                                props.onInput(
                                    props.column.name,
                                    $event.detail.value
                                )
                            "
                        />
                    </template>
                    <template v-slot:selection="props">
                        <td>
                            <div class="checkbox">
                                <input
                                    v-model="props.item.selected"
                                    type="checkbox"
                                    :name="'selection-' + props.item.productId"
                                    :true-value="true"
                                    :false-value="false"
                                />
                            </div>
                        </td>
                    </template>
                    <template v-slot:actions="props">
                        <td>
                            <i
                                class="fa fa-info-circle back"
                                @click="goToProductPage(props.item.productId)"
                            />
                        </td>
                    </template>
                    <template v-slot:orderNr="props">
                        <td>
                            {{ props.item.orderNr }}
                        </td>
                    </template>
                    <template v-slot:customer="props">
                        <td>
                            {{ props.item.customerName }}
                        </td>
                    </template>
                    <template v-slot:deliveryDate="props">
                        <td>
                            {{ props.item.deliveryDateDay }}
                        </td>
                    </template>
                    <template
                        v-slot:footer
                        v-if="status === 'success' && products.length > 0"
                    >
                        <tr>
                            <th class="sticky">
                                <div class="multiselect-actions">
                                    <div class="checkbox">
                                        <input
                                            id="select-all"
                                            @change="selectAll"
                                            type="checkbox"
                                            name="select-all"
                                            :true-value="true"
                                            :false-value="false"
                                        />
                                    </div>
                                    <div
                                        v-if="products.some((x) => x.selected)"
                                        @click="calculateOptimizedList"
                                        class="multiselect-action"
                                    >
                                        <i class="fa fa-calculator" />
                                        {{
                                            trans(
                                                'pages.canvas_work_overview.calculate_optimized_canvas_bond'
                                            )
                                        }}
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </template>
                </crm-table>
            </div>
        </div>
    </div>
</template>
<script>
import Spinner from '../components/loader/Spinner';
import CrmTable from '../components/CrmTable';
import TextInput from '../components/TextInput';
import AutoCompleteInput from '../components/AutoCompleteInput';
import { mapGetters } from 'vuex';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import productListApi from '../api/productList';
import productListDefinitionApi from '../api/productListDefinition';
import optimizedProductListApi from '../api/optimizedProductList';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import ErrorHelper from '../helpers/ErrorHelper';
import { sortEmptyOptions, clearedFilters } from '../shared/canvasWorkOverview';
import { colorArray } from '../shared/colors';
import axios from 'axios';
import IsDebugMixin from '../mixins/is-debug';
import localization from '../mixins/localization';

export default {
    mixins: [NavigationFunctionsMixin, IsDebugMixin, localization],
    components: { Spinner, CrmTable, TextInput, AutoCompleteInput },
    data() {
        return {
            definitions: null,
            initialFilters: null,
            status: 'running',
            products: [],
            columns: [
                {
                    name: 'selection',
                    label: '',
                },
                {
                    name: 'actions',
                    label: this.trans('pages.canvas_work_overview.actions'),
                },
                {
                    name: 'productlistId',
                    label: this.trans(
                        'pages.canvas_work_overview.canvas_bond_nr'
                    ),
                    searchable: true,
                    sortable: true,
                    hideColumn: !this.isDebug,
                },
                {
                    name: 'productNr',
                    label: this.trans('pages.canvas_work_overview.product_nr'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'orderNr',
                    label: this.trans('pages.canvas_work_overview.order_nr'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'customer',
                    label: this.trans('pages.canvas_work_overview.customer'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'reference',
                    label: this.trans('pages.canvas_work_overview.reference'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'canvasType',
                    label: this.trans('pages.canvas_work_overview.canvas_type'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'canvasCode',
                    label: this.trans('pages.canvas_work_overview.canvas_nr'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'canvasColor',
                    label: this.trans('pages.canvas_work_overview.color'),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'deliveryDate',
                    label: this.trans(
                        'pages.canvas_work_overview.delivery_date'
                    ),
                    searchable: true,
                    sortable: true,
                },
                {
                    name: 'sortEmpty',
                    label: this.trans(
                        'pages.canvas_work_overview.empty_or_all'
                    ),
                    hideColumn: true,
                    sortable: false,
                    searchable: true,
                },
                {
                    name: 'clearFilters',
                    label: 'clearFilters',
                    searchable: true,
                    hideColumn: true,
                },
            ],
        };
    },
    computed: {
        sortEmptyOptions: () => sortEmptyOptions,
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        onFetchDebounced() {
            return _.debounce(this.onFetch, 500);
        },
        definitionOptions() {
            if (!this.definitions) {
                return null;
            }
            return this.definitions.map((x) => ({
                label: x.label,
                value: x.key,
            }));
        },
    },
    methods: {
        getRowStyle(product) {
            const pastDue = product.daysPastDue;
            return {
                'background-color':
                    colorArray[Math.min(Math.max(pastDue, 0), 9)],
            };
        },
        clearFilters() {
            this.$refs.canvasWorkOverviewTable.originalFilters = {
                ...clearedFilters,
            };
            this.$refs.canvasWorkOverviewTable.clearFilters();
        },
        selectAll($event) {
            const value = $event.target.checked;
            this.products.forEach((x) => {
                x.selected = value;
            });
        },
        calculateOptimizedList() {
            const selectedProducts = this.products.filter((x) => x.selected);
            const ids = selectedProducts.map((x) => x.productlistId);
            optimizedProductListApi
                .calculate(ids)
                .then((response) => {
                    const productIds = selectedProducts.map((x) => x.productNr);
                    showInfoMessage(
                        this.trans(
                            'pages.canvas_work_overview.optimized_canvas_bond_calculated',
                            {
                                productIds: productIds.join(', '),
                            }
                        )
                    );
                    this.$router.push(
                        '/optimizedproductlistdetails/' + response.data
                    );
                })
                .catch((error) => {
                    showErrorMessage(
                        ErrorHelper.parseError(error.response.data)
                    );
                    this.$refs.canvasWorkOverviewTable.fetch(false);
                });
        },
        onFetch(filters) {
            if (filters.page === 1) {
                this.products = [];
            }

            const payload = {
                ...filters,
                year: null,
                offset: this.products.length,
                limit: 50,
                sortOrder: filters.order,
                sortBy: filters.sort,
                productlistStatusKey: 'NEW',
                productlistDefinitionKey: 'DOEKBON',
            };

            this.$store.commit(
                'pleatingOverview/setFilters',
                { ...payload },
                { root: true }
            );

            this.status = 'running';
            productListApi
                .overview(payload)
                .then((response) => {
                    const products = response.data.data;
                    this.newCount = products.length;
                    products.forEach((product) => {
                        product.selected = false;
                    });
                    this.products = [...this.products, ...products];
                    this.status = 'success';
                })
                .catch((err) => {
                    console.error(err);

                    if (!axios.isCancel(err)) {
                        showErrorMessage(
                            this.trans(
                                'pages.canvas_work_overview.unknown_error'
                            )
                        );
                        this.status = 'error';
                    }
                });
        },
    },
    mounted() {
        const filters = this.$store.getters['canvasWorkOverview/getFilters'];

        productListDefinitionApi.fetchAll().then((response) => {
            this.definitions = response.data;

            const keys = this.definitions.map((x) => x.key);
            if (!keys.includes(filters.productlistDefinitionKey) && keys[0]) {
                filters.productlistDefinitionKey = keys[0];
            }
            this.initialFilters = filters;
        });
    },
};
</script>
